import { useState } from 'react'
import { createPortal } from 'react-dom'
import { Modal } from 'react-bootstrap'
import { KTSVG } from '_metronic/helpers'
import moment from 'moment'
import { toast } from 'react-toastify'
import { useProjectStore } from 'Store/Project'
import { shallow } from 'zustand/shallow'
import { JobClosingDocument, JobClosingExpenseLine, JobClosingPileLine } from '../types'
import './JobClosingDocumentPreview.scss'

interface Props {
  isOpen: boolean
  projectId: string
  division?: string
  jobClosingDocument: JobClosingDocument | null
  signedFileUrl?: string | null
  onClose: () => void
}

const COMPANY_ADDRESS_TH =
  '1339 อาคารอาวรรณ ถนนประชาราษฎร์ สาย 1 แขวงวงศ์สว่าง เขตบางซื่อ กรุงเทพฯ 10800'
const COMPANY_ADDRESS_EN =
  '1339 ARWAN BUILDING, PRACHARAT 1 RD., WONGSAWANG, BANGSUE DISTRICT, BANGKOK 10800, THAILAND.'
const COMPANY_CONTACT =
  'Tel. 02-587-0198-200 (AUTO-32 LINES) Fax. (662) 02-587-4761, 02-587-4763, 02-587-4774 www.paco-group.com'
const PACO_COMPANY_SHORT = 'ปทุมธานีคอนกรีต จำกัด'

const JobClosingDocumentPreview: React.FC<Props> = ({
  isOpen,
  projectId,
  division = '',
  jobClosingDocument,
  signedFileUrl = null,
  onClose,
}) => {
  const modalsRoot = document.getElementById('root-modals') || document.body
  const [isDownloading, setIsDownloading] = useState(false)
  const downloadJobClosingPdf = useProjectStore(state => state.downloadJobClosingPdf, shallow)

  if (!jobClosingDocument) return null

  const createdAt = moment(jobClosingDocument.createdAt)
  const buddhistYear = createdAt.year() + 543
  const monthLabel = createdAt.locale('th').format('MMMM')
  const signedUploadedAt = moment(jobClosingDocument.signedUploadedAt)
  const showSignedBlock =
    Boolean(signedFileUrl && jobClosingDocument.hasSignedFile)

  const handlePrint = async () => {
    if (!projectId || isDownloading) return
    setIsDownloading(true)
    try {
      const fileName = `job-closing-${jobClosingDocument.documentNo}.pdf`
      const { success, data } = await downloadJobClosingPdf(
        parseInt(projectId, 10),
        jobClosingDocument.id,
        fileName
      )
      if (!success) {
        toast.error(data || 'ดาวน์โหลด PDF ไม่สำเร็จ')
      }
    } catch {
      toast.error('ดาวน์โหลด PDF ไม่สำเร็จ')
    } finally {
      setIsDownloading(false)
    }
  }

  return createPortal(
    <Modal
      id='kt_modal_job_closing_preview'
      tabIndex={-1}
      aria-hidden='true'
      dialogClassName='modal-dialog modal-dialog-centered modal-dialog-scrollable mw-900px'
      show={isOpen}
      onHide={onClose}
      backdrop={isDownloading ? 'static' : true}
      keyboard={!isDownloading}>
      <div className='modal-content'>
        <div className='modal-header'>
          <h2>ใบปิดงาน — {jobClosingDocument.documentNo}</h2>
          {!isDownloading && (
            <div className='btn btn-sm btn-icon btn-active-light-danger' onClick={onClose}>
              <KTSVG className='svg-icon-1' path='/media/icons/duotune/general/gen040.svg' />
            </div>
          )}
        </div>

        <div className='modal-body p-10 overflow-auto' id='job-closing-document-preview'>
          <div className='border border-gray-300 p-8 bg-white text-gray-800 job-closing-preview-document'>
            {/* Header — paco-logo.png is the full letterhead banner */}
            <div className='d-flex justify-content-between align-items-start gap-4 mb-6'>
              <div className='flex-grow-1'>
                <img
                  src='/media/logos/paco-logo.png'
                  alt='PACO — บริษัท ปทุมธานีคอนกรีต จำกัด'
                  className='w-100'
                  style={{ maxHeight: 90, objectFit: 'contain', objectPosition: 'left center' }}
                  onError={(e: any) => {
                    e.target.style.display = 'none'
                    const fallback = e.target.nextElementSibling
                    if (fallback) fallback.style.display = 'block'
                  }}
                />
                <div style={{ display: 'none' }}>
                  <div className='fw-bold fs-3' style={{ color: '#1a4a8a' }}>
                    บริษัท ปทุมธานีคอนกรีต จำกัด
                  </div>
                  <div className='fw-bold fs-6' style={{ color: '#1a4a8a' }}>
                    THE PATHUMTHANI CONCRETE CO., LTD.
                  </div>
                  <div className='text-gray-600' style={{ fontSize: 11, lineHeight: 1.5 }}>
                    <div>{COMPANY_ADDRESS_TH}</div>
                    <div>{COMPANY_ADDRESS_EN}</div>
                    <div>{COMPANY_CONTACT}</div>
                  </div>
                </div>
              </div>
              <div
                className='border border-gray-400 p-3 text-nowrap fs-7'
                style={{ minWidth: 160 }}>
                <div className='mb-1'>
                  วันที่ <span className='fw-bold'>{createdAt.format('DD')}</span>
                </div>
                <div className='mb-1'>
                  เดือน <span className='fw-bold'>{monthLabel}</span>
                </div>
                <div>
                  พ.ศ. <span className='fw-bold'>{buddhistYear}</span>
                </div>
              </div>
            </div>

            {/* Intro */}
            <div className='mb-5 fs-7' style={{ lineHeight: 1.8 }}>
              <div className='mb-1'>
                <span className='fw-bold'>เรื่อง</span>{' '}
                {jobClosingDocument.subject || 'ขอให้ตรวจ และรับมอบงานตอกเสาเข็ม'}
              </div>
              <div className='mb-1'>
                <span className='fw-bold'>เรียน</span>{' '}
                {jobClosingDocument.addressee || 'ผู้ควบคุมงานตอกเสาเข็มคอนกรีตอัดแรง'}
              </div>
              <div>
                <span className='fw-bold'>บริษัท / หจก.</span>{' '}
                {jobClosingDocument.companyName || '................................'}
              </div>
            </div>

            <p className='text-gray-700 fs-7 mb-5' style={{ textIndent: '2em', lineHeight: 1.8 }}>
              ตามที่บริษัทฯ ได้จัดปั้นจั่นเครื่องจักรมาประกอบ ณ หน่วยงาน{' '}
              {division || '................................'} ที่ท่านควบคุมงานอยู่นี้
              เพื่อทำการตอกเสาเข็มคอนกรีตอัดแรงของบริษัท {PACO_COMPANY_SHORT}{' '}
              ดังรายละเอียดต่อไปนี้ :-
            </p>

            {/* Lines table */}
            <div className='table-responsive mb-5'>
              <table
                className='table table-bordered table-sm job-closing-preview-table mb-0'
                style={{ minWidth: 480 }}>
                <thead className='bg-light'>
                  <tr className='text-center fs-7 fw-bold'>
                    <th style={{ width: 60 }}>ลำดับ</th>
                    <th>ชนิดและขนาดของเสาเข็ม</th>
                    <th style={{ width: 110 }}>ความยาว (เมตร)</th>
                    <th style={{ width: 100 }}>จำนวน (ต้น)</th>
                  </tr>
                </thead>
                <tbody>
                  {jobClosingDocument.lines.map(line => {
                    if (line.lineType === 'pile') {
                      const pileLine = line as JobClosingPileLine
                      return (
                        <tr key={line.id} className='fs-7'>
                          <td className='text-center'>{line.sequence}</td>
                          <td>{pileLine.inventoryCode}</td>
                          <td className='text-center'>
                            {pileLine.length != null ? pileLine.length : '—'}
                          </td>
                          <td className='text-center'>{pileLine.quantity}</td>
                        </tr>
                      )
                    }
                    const expenseLine = line as JobClosingExpenseLine
                    return (
                      <tr key={line.id} className='fs-7'>
                        <td className='text-center'>{line.sequence}</td>
                        <td>{expenseLine.description}</td>
                        <td className='text-center'>—</td>
                        <td className='text-center'>
                          {expenseLine.amount != null ? expenseLine.amount : '—'}
                        </td>
                      </tr>
                    )
                  })}
                  {jobClosingDocument.lines.length === 0 && (
                    <tr className='fs-7'>
                      <td colSpan={4} className='text-center text-muted py-6'>
                        ไม่มีรายการ
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <p className='text-gray-700 fs-7 mb-6' style={{ textIndent: '2em', lineHeight: 1.8 }}>
              บริษัทฯ ขอเรียนให้ท่านทราบว่า ขณะนี้ปั้นจั่นเครื่องจักรซึ่งบริษัทฯ จัดเข้าหน่วยงาน
              ได้ทำการตอกเสาเข็ม จำนวนดังกล่าวข้างต้นนี้เสร็จเรียบร้อย และครบถูกต้องแล้ว
              จึงขอความกรุณาจากท่านให้ตรวจสอบขนาด / จำนวนเสาเข็ม และลงนามรับมอบงาน
            </p>

            <div className='d-flex justify-content-end mb-8 fs-7'>
              <div className='job-closing-sign-off-block text-center'>
                <div className='mb-4'>ขอแสดงความนับถือ</div>
                {showSignedBlock ? (
                  <>
                    <img
                      src={signedFileUrl!}
                      alt='ลายเซ็น'
                      className='job-closing-preview-signature'
                    />
                    {jobClosingDocument.signedByName && (
                      <div className='mt-2 job-closing-sign-off-name'>
                        ( {jobClosingDocument.signedByName} )
                      </div>
                    )}
                    {signedUploadedAt.isValid() && (
                      <div className='mt-1 job-closing-sign-off-date'>
                        วันที่ {signedUploadedAt.format('DD/MM/YYYY')}
                      </div>
                    )}
                  </>
                ) : (
                  <div className='border-bottom border-gray-500 mb-1' style={{ height: 32, minWidth: 200 }} />
                )}
              </div>
            </div>

            {/* Acceptance footer */}
            <div className='fs-7' style={{ lineHeight: 1.9 }}>
              <div className='mb-2 d-flex flex-wrap align-items-center gap-4'>
                <span className='fw-bold'>- การตรวจรับมอบงาน</span>
                <span className='d-inline-flex align-items-center gap-2'>
                  <span
                    className='d-inline-flex align-items-center justify-content-center border border-dark'
                    style={{ width: 16, height: 16, fontSize: 11, lineHeight: 1 }}>
                    {jobClosingDocument.remark ? '✓' : ''}
                  </span>
                  <span>
                    งวดที่{' '}
                    <span className='fw-bold text-decoration-underline px-2'>
                      {jobClosingDocument.remark || '........'}
                    </span>
                  </span>
                </span>
                <span className='d-inline-flex align-items-center gap-2'>
                  <span
                    className='d-inline-block border border-dark'
                    style={{ width: 16, height: 16 }}
                  />
                  <span>งวดปิดงาน</span>
                </span>
              </div>

              <div className='mb-6'>
                - ได้ตรวจสอบแล้ว ชนิดและจำนวนเสาที่ตอกครบถูกต้อง และผลงานเรียบร้อยถูกต้องทุกประการ
              </div>

              <div className='row g-4'>
                <div className='col-12'>
                  <div className='mb-4'>
                    ลงชื่อ{' '}
                    <span className='d-inline-block border-bottom border-gray-500' style={{ minWidth: 180 }} />
                  </div>
                  <div className='mb-1 text-muted' style={{ paddingLeft: '3rem' }}>
                    ผู้ควบคุมงานตอกเสาเข็ม
                  </div>
                  <div className='mb-4'>
                    ({' '}
                    <span className='d-inline-block border-bottom border-gray-500' style={{ minWidth: 160 }}>
                      {' '}
                    </span>{' '}
                    )
                    <div className='text-muted fs-8' style={{ paddingLeft: '1rem' }}>
                      ชื่อตัวบรรจง
                    </div>
                  </div>
                  <div className='job-closing-acceptance-date d-flex flex-wrap align-items-end gap-1'>
                    <span className='text-nowrap'>วันที่</span>
                    <span className='job-closing-date-field border-bottom border-gray-500 text-center' />
                    <span>/</span>
                    <span className='job-closing-date-field border-bottom border-gray-500 text-center' />
                    <span>/</span>
                    <span className='job-closing-date-field job-closing-date-field-year border-bottom border-gray-500 text-center' />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className='modal-footer'>
          <button
            type='button'
            className='btn btn-light fw-bold'
            disabled={isDownloading}
            onClick={onClose}>
            ปิด
          </button>
          <button
            type='button'
            className='btn btn-primary fw-bold'
            disabled={isDownloading}
            onClick={handlePrint}>
            {isDownloading ? (
              <>
                <span className='spinner-border spinner-border-sm me-2' />
                กำลังดาวน์โหลด...
              </>
            ) : (
              'พิมพ์'
            )}
          </button>
        </div>
      </div>
    </Modal>,
    modalsRoot
  )
}

export default JobClosingDocumentPreview
